import { getIdentityUser } from '@cenorion/api/generated/hooks';

import { reportError } from '@/core/errors/report-error';

import { profileFromClaims } from './profile-from-claims';
import { useSessionStore, type SessionUser } from './session-store';

/**
 * The caller's roles and permissions, as the identity service resolves them. `permissions` is the
 * set the edge authorizer grants these roles under the tenant's role definitions (assignable roles
 * only, base-role rule included), so the portal mirrors it verbatim and resolves nothing itself.
 * A body without the array fails closed and loudly — read as `[]`, a contract mismatch would pass
 * for a user granted nothing.
 */
async function readIdentityGrants(
  userId: string,
): Promise<Pick<SessionUser, 'roles' | 'permissions'>> {
  const { user, permissions } = await getIdentityUser(userId);
  if (!Array.isArray(permissions)) throw new Error('user detail carried no permissions');
  return { roles: user.roles, permissions };
}

/**
 * The session profile the app boots with: identity from the id_token claims, roles and permissions
 * from the identity store. Runs inside profile adoption, so `authReady` resolves only once the
 * permission set is known — the ability is seeded from it before the router's first loader, and
 * nothing gated renders against a partial set. A failure here is the terminal session error, not a
 * deny-all session: the tokens are valid, so a silent empty ability would look like a broken portal.
 */
export async function profileWithPermissions(): Promise<SessionUser> {
  const profile = await profileFromClaims();
  return { ...profile, ...(await readIdentityGrants(profile.id)) };
}

let resyncInFlight: Promise<void> | null = null;

/**
 * Re-reads the grants after a token refresh, so a role change reaches the portal at the next
 * refresh rather than the next sign-in. Single-flight: a re-sync request that itself answers 401
 * triggers a refresh, which must not start a second re-sync behind it.
 *
 * Applied only to the session it was started for — a sign-out, idle logout or user switch while the
 * reads were in flight leaves the store as it is, rather than reviving a session that has ended. A
 * failure keeps the last known grants: the backend still enforces, and blanking a working session
 * over a transient read is worse than a permission set one refresh old.
 */
export function resyncPermissions(): Promise<void> {
  const started = useSessionStore.getState();
  if (started.status !== 'authenticated' || !started.user) return Promise.resolve();
  const userId = started.user.id;

  resyncInFlight ??= (async () => {
    try {
      const grants = await readIdentityGrants(userId);
      const current = useSessionStore.getState();
      if (current.status !== 'authenticated' || current.user?.id !== userId) return;
      current.setAuthenticated({ ...current.user, ...grants });
    } catch (cause) {
      reportError(cause, { errorKind: 'permissions_resync_failed' });
    } finally {
      resyncInFlight = null;
    }
  })();
  return resyncInFlight;
}
